<template>
  <div>
    <Loading v-if="isLoading" />

    <v-card v-if="validBooking" tile elevation="0" flat v-show="!isLoading" class="pb-5">
      <template slot="progress">
        <v-progress-linear
          color="deep-purple"
          height="10"
          indeterminate
        ></v-progress-linear>
      </template>
      <!-- Mapa de google --->
      <gmap
        v-if="validCoords  && driverMap=='gmap'"
        :origin="map.origin"
        :destination="map.destination"
      />
      <leafletMap
        v-if="validCoords  && driverMap=='leaflet'"
        :origin="map.origin"
        :destination="map.destination"
      />
      <!-- Modern Stepper Header -->
      <div class="booking-header" v-if="booking.trackingStatus !== 'noShow'">
        <!-- Current Step Display -->
        <div class="current-step-container">
          <div class="step-progress-dots">
            <span
              v-for="(step, index) in statuses"
              :key="index"
              class="progress-dot"
              :class="{
                'dot-completed': index < currentStepIndex,
                'dot-active': index === currentStepIndex - 1
              }"
            ></span>
          </div>
          <div class="current-step-name">
            {{ $t("lblTrackingStatus." + booking.trackingStatus) }}
          </div>
        </div>

        <!-- Service Info Card -->
        <div class="service-info-card">
          <!-- Date & Time + Flight Status -->
          <div class="info-row datetime-row" :class="{ 'info-updated': flightInfoUpdated }">
            <div class="datetime-left">
              <div class="info-item">
                <v-icon size="18" color="#6200ea">mdi-calendar</v-icon>
                <span class="info-value" :class="{ 'today-highlight': isToday }">
                  {{ formattedPickupDate }}
                </span>
              </div>
              <div class="info-divider"></div>
              <div class="info-item">
                <v-icon size="18" color="#6200ea">mdi-clock-outline</v-icon>
                <span class="info-value">{{ booking.pickupDatetime | moment("HH:mm") }}</span>
              </div>
            </div>
            <!-- Flight Status Pill -->
            <div
              v-if="booking.flightInfo && booking.trip !== 'P2P'"
              class="flight-status-pill"
              :class="'flight-' + getFlightStatusClass(booking.flightInfo)"
            >
              <v-icon size="12">{{ flightStatusIcon(booking.flightInfo) }}</v-icon>
              <span>{{ flightStatusText(booking.flightInfo) }}</span>
            </div>
          </div>

          <!-- Client Info -->
          <div class="client-row">
            <div class="client-info">
              <div class="client-avatar">
                <v-icon size="20" color="white">mdi-account</v-icon>
              </div>
              <span class="client-name notranslate">{{ clientName }}</span>
            </div>
            <button
              v-if="(!isPending) && this.$store.state.ui.online"
              class="chat-btn"
              @click.prevent="chat(booking.id)"
            >
              <v-icon size="18" color="white">mdi-message-text</v-icon>
              <span>Chat</span>
            </button>
          </div>
        </div>
      </div>


      <!-- Datos de reserva -->
      <div class="reservation-section">
        <!-- Header: ID y Status -->
        <div class="reservation-row">
          <span class="res-id">ID {{ booking.serviceNumber }}</span>
          <span class="status-pill" :class="'status-' + booking.status">{{ bookingStatus.label }}</span>
        </div>

        <!-- PAX + Service Type + Channel -->
        <div class="reservation-row">
          <div class="pax-inline">
            <span class="pax-total">{{ booking.reservation.adults + (booking.reservation.children || 0) + (booking.reservation.infants || 0) }} PAX</span>
            <span class="pax-breakdown">
              <v-icon x-small color="#999">mdi-account</v-icon>{{ booking.reservation.adults }}
              <v-icon x-small color="#999" class="ml-2">mdi-human-child</v-icon>{{ booking.reservation.children || 0 }}
              <v-icon x-small color="#999" class="ml-2">mdi-baby-carriage</v-icon>{{ booking.reservation.infants || 0 }}
            </span>
          </div>
        </div>

        <div class="reservation-row">
          <span class="res-service">{{ booking.serviceType.name }}</span>
          <span class="res-channel">{{ booking.salesChannel.name }}</span>
        </div>

      </div>
      <!-- Fin datos de reserva -->

      <!-- Route Section -->
      <div class="route-section">
        <div class="route-wrapper">
          <div class="route-line"></div>
          <div class="route-content">
            <div class="route-point">
              <div class="route-dot dot-origin"></div>
              <div class="route-info">
                <span class="route-label">{{ $t("booking.pickupOn") }}</span>
                <span class="route-name">{{ booking.pickup.name }}</span>
              </div>
            </div>
            <div class="route-point">
              <div class="route-dot dot-destination"></div>
              <div class="route-info">
                <span class="route-label">{{ $t("booking.dropoffAt") }}</span>
                <span class="route-name">{{ booking.dropoff.name }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Flight & Notes Section -->
      <div class="details-section">
        <template v-if="booking.trip !== 'P2P'">
          <!-- Flight Info Card -->
          <div class="flight-card my-2" :class="{ 'info-updated': flightInfoUpdated, 'flight-card-compact': !booking.flightInfo }" @click="openFlightModal">
            <!-- Header: Flight number + Status + Time (compact when no flightInfo) -->
            <div class="flight-card-header">
              <div class="flight-main-info">
                <v-icon color="#666" class="mr-2" :size="booking.flightInfo ? 24 : 20">mdi-airplane</v-icon>
                <span class="flight-number">{{ booking.flightNumber }}</span>
                <v-icon small class="pulse-icon ml-1" color="primary">mdi-radar</v-icon>
              </div>
              <!-- Show time inline when no flightInfo -->
              <span v-if="!booking.flightInfo && booking.flightDatetime" class="flight-time-inline">
                <v-icon x-small color="#666" class="mr-1">mdi-clock-outline</v-icon>
                {{ booking.flightDatetime | moment("HH:mm") }}
              </span>
              <v-chip
                v-if="booking.flightInfo"
                :color="flightStatusColor(booking.flightInfo)"
                text-color="white"
                x-small
                label
              >
                {{ flightStatusText(booking.flightInfo) }}
              </v-chip>
              <v-icon color="#ccc" size="20">mdi-chevron-right</v-icon>
            </div>

            <!-- Body: Flight details (only when flightInfo exists) -->
            <div class="flight-card-body" v-if="booking.flightInfo">
              <div class="flight-times">
                <!-- Original flight time -->
                <div class="flight-time-item" v-if="booking.flightInfo.flightDatetime">
                  <v-icon x-small color="#999">mdi-airplane-clock</v-icon>
                  <span class="flight-time-label">{{ $t("flightModal.originalTime") }}</span>
                  <span class="flight-time-value">{{ booking.flightInfo.flightDatetime | moment("HH:mm") }}</span>
                </div>

                <!-- Estimated landing -->
                <div class="flight-time-item" v-if="booking.flightInfo.estimatedLanding && !booking.flightInfo.hasLanded">
                  <v-icon x-small color="#1565c0">mdi-airplane-landing</v-icon>
                  <span class="flight-time-label">{{ $t("flightModal.estimatedLanding") }}</span>
                  <span class="flight-time-value highlight">{{ booking.flightInfo.estimatedLanding | moment("HH:mm") }}</span>
                </div>

                <!-- Estimated gate arrival -->
                <div class="flight-time-item" v-if="booking.flightInfo.estimatedGateArrival">
                  <v-icon x-small color="#999">mdi-door</v-icon>
                  <span class="flight-time-label">{{ $t("flightModal.estimatedGate") }}</span>
                  <span class="flight-time-value">{{ booking.flightInfo.estimatedGateArrival | moment("HH:mm") }}</span>
                </div>
              </div>

              <!-- Progress + Terminal -->
              <div class="flight-meta" v-if="booking.flightInfo.progressPercent !== undefined || booking.flightInfo.terminal">
                <span v-if="booking.flightInfo.progressPercent !== undefined && !(booking.flightInfo.hasLanded && booking.flightInfo.progressPercent < 100)" class="flight-meta-item">
                  <v-icon x-small color="#999">mdi-chart-line-variant</v-icon>
                  {{ $t("flightModal.progress") }}: {{ booking.flightInfo.progressPercent }}%
                </span>
                <span v-if="booking.flightInfo.terminal" class="flight-meta-item">
                  <v-icon x-small color="#999">mdi-door</v-icon>
                  {{ $t("flightModal.terminal") }}: T{{ booking.flightInfo.terminal }}
                </span>
              </div>
            </div>
          </div>
        </template>

        <!-- Notes -->
        <div class="notes-row" v-if="booking.supplierNotes">
          <v-icon size="16" color="#999">mdi-information-outline</v-icon>
          <div class="notes-content">
            <span class="notes-label">{{ $t("booking.notes") }}</span>
            <span class="notes-text">{{ booking.supplierNotes }}</span>
          </div>
        </div>
      </div>
      
    </v-card>

    <!-- Scroll indicator -->
    <div class="scroll-hint" v-show="showScrollHint" @click="scrollToBottom">
      <v-icon size="18" color="#fff">mdi-chevron-down</v-icon>
    </div>

    <!-- Modal de información del vuelo -->
    <v-dialog v-model="flightModal" max-width="400" scrollable>
      <!-- Contenido cuando hay datos de vuelo -->
      <v-card class="flight-modal-card" v-if="booking.flightInfo && hasFlightData">
        <v-card-title class="flight-modal-header pa-4">
          <div class="d-flex align-center">
            <v-avatar color="indigo" size="40" class="mr-3">
              <v-icon dark>mdi-airplane</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">{{ booking.flightNumber }}</div>
              <div class="text-caption">{{ $t("flightModal.title") }}</div>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="reloadFlightInfo" :loading="loadingFlightInfo" class="mr-1">
            <v-icon>mdi-reload</v-icon>
          </v-btn>
          <v-btn icon @click="flightModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-0">
          <!-- Estado del vuelo -->
          <div class="flight-status-section pa-4">
            <v-chip
              :color="flightStatusColor(booking.flightInfo)"
              text-color="white"
              class="mb-3"
              label
            >
              <v-icon left>{{ flightStatusIcon(booking.flightInfo) }}</v-icon>
              {{ flightStatusText(booking.flightInfo) }}
            </v-chip>

            <!-- Barra de progreso -->
            <div v-if="booking.flightInfo.progressPercent !== undefined" class="mb-3">
              <div class="text-caption grey--text mb-1">
                <strong>{{ $t("flightModal.progress") }}:</strong> {{ booking.flightInfo.progressPercent }}%
              </div>
              <div style="background-color: #e9ecef; border-radius: 4px; height: 6px;">
                <div
                  style="background-color: #6200ea; border-radius: 4px; height: 6px; transition: width 0.3s ease;"
                  :style="{ width: booking.flightInfo.progressPercent + '%' }"
                ></div>
              </div>
            </div>

            <!-- Origen / Destino -->
            <div v-if="booking.flightInfo.originAirport || booking.flightInfo.destinationAirport" class="d-flex justify-space-between align-center">
              <div class="text-center">
                <div class="text-h6 font-weight-bold">{{ booking.flightInfo.originAirport || '---' }}</div>
                <small v-if="booking.flightInfo.originCity" class="grey--text">{{ booking.flightInfo.originCity }}</small>
              </div>
              <div class="text-center">
                <v-icon color="grey">mdi-arrow-right</v-icon>
              </div>
              <div class="text-center">
                <div class="text-h6 font-weight-bold">{{ booking.flightInfo.destinationAirport || '---' }}</div>
                <small v-if="booking.flightInfo.destinationCity" class="grey--text">{{ booking.flightInfo.destinationCity }}</small>
              </div>
            </div>
          </div>

          <v-divider></v-divider>

          <!-- Detalles del vuelo -->
          <v-list dense class="pa-0">
            <!-- Hora programada -->
            <v-list-item v-if="booking.flightDatetime">
              <v-list-item-icon class="mr-3">
                <v-icon color="grey">mdi-clock-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t("flightModal.scheduledTime") }}</v-list-item-subtitle>
                <v-list-item-title class="font-weight-medium">
                  {{ booking.flightInfo.flightDatetime | moment("HH:mm") }} hrs
                  <span class="text-caption grey--text ml-1">
                    ({{ booking.flightDatetime | moment("D MMM") }})
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Aterrizaje estimado -->
            <v-list-item v-if="booking.flightInfo.estimatedLanding">
              <v-list-item-icon class="mr-3">
                <v-icon color="indigo">mdi-airplane-landing</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t("flightModal.estimatedLanding") }}</v-list-item-subtitle>
                <v-list-item-title class="font-weight-medium">
                  {{ booking.flightInfo.estimatedLanding | moment("HH:mm") }} hrs
                  <span class="text-caption grey--text ml-1">
                    ({{ booking.flightInfo.estimatedLanding | moment("D MMM") }})
                  </span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Aterrizaje real -->
            <v-list-item v-if="booking.flightInfo.actualLanding">
              <v-list-item-icon class="mr-3">
                <v-icon color="success">mdi-airplane-landing</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t("flightModal.actualLanding") }}</v-list-item-subtitle>
                <v-list-item-title class="font-weight-medium success--text">
                  {{ booking.flightInfo.actualLanding | moment("HH:mm") }} hrs
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Llegada a puerta estimada -->
            <v-list-item v-if="booking.flightInfo.estimatedGateArrival">
              <v-list-item-icon class="mr-3">
                <v-icon color="indigo">mdi-clock-outline</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t("flightModal.estimatedGate") }}</v-list-item-subtitle>
                <v-list-item-title class="font-weight-medium">
                  {{ booking.flightInfo.estimatedGateArrival | moment("HH:mm") }} hrs
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Llegada real a puerta -->
            <v-list-item v-if="booking.flightInfo.actualGateArrival">
              <v-list-item-icon class="mr-3">
                <v-icon color="success">mdi-clock-check</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t("flightModal.actualGate") }}</v-list-item-subtitle>
                <v-list-item-title class="font-weight-medium success--text">
                  {{ booking.flightInfo.actualGateArrival | moment("HH:mm") }} hrs
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>

            <!-- Terminal y Puerta -->
            <v-list-item v-if="booking.flightInfo.terminal || booking.flightInfo.gate">
              <v-list-item-icon class="mr-3">
                <v-icon color="indigo">mdi-gate</v-icon>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-subtitle>{{ $t("flightModal.terminal") }} / {{ $t("flightModal.gate") }}</v-list-item-subtitle>
                <v-list-item-title class="font-weight-medium">
                  <span v-if="booking.flightInfo.terminal">T{{ booking.flightInfo.terminal }}</span>
                  <span v-else class="grey--text">{{ $t("flightModal.na") }}</span>
                  <span class="mx-2">|</span>
                  <span v-if="booking.flightInfo.gate">{{ booking.flightInfo.gate }}</span>
                  <span v-else class="grey--text">{{ $t("flightModal.na") }}</span>
                </v-list-item-title>
              </v-list-item-content>
            </v-list-item>
          </v-list>

          <!-- Fuente de datos y última actualización -->
          <div class="flight-metadata pa-3">
            <div v-if="booking.flightInfo.dataSource" class="d-flex align-center mb-1">
              <v-icon x-small color="grey" class="mr-2">mdi-database</v-icon>
              <span class="text-caption grey--text">
                {{ $t("flightModal.dataSource") }}: <strong>{{ booking.flightInfo.dataSource }}</strong>
              </span>
            </div>
            <div v-if="booking.flightInfo.lastUpdateAt" class="d-flex align-center">
              <v-icon x-small color="grey" class="mr-2">mdi-update</v-icon>
              <span class="text-caption grey--text">
                {{ $t("flightModal.lastUpdate") }}: <strong>{{ booking.flightInfo.lastUpdateAt | moment("D MMM HH:mm") }}</strong>
              </span>
            </div>
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-3">
          <v-btn
            v-if="booking.flightStatsLink && booking.flightStatsLink !== '#'"
            color="indigo"
            outlined
            block
            :href="booking.flightStatsLink"
            target="_blank"
          >
            <v-icon left>mdi-open-in-new</v-icon>
            {{ $t("flightModal.viewOnFlightStats") }}
          </v-btn>
        </v-card-actions>
      </v-card>

      <!-- Contenido cuando NO hay datos de vuelo -->
      <v-card class="flight-modal-card" v-else>
        <v-card-title class="flight-modal-header pa-4">
          <div class="d-flex align-center">
            <v-avatar color="grey" size="40" class="mr-3">
              <v-icon dark>mdi-airplane</v-icon>
            </v-avatar>
            <div>
              <div class="text-h6 font-weight-bold">{{ booking.flightNumber }}</div>
              <div class="text-caption">{{ $t("flightModal.title") }}</div>
            </div>
          </div>
          <v-spacer></v-spacer>
          <v-btn icon @click="flightModal = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="pa-6 text-center">
          <v-icon size="64" color="grey lighten-1" class="mb-4">mdi-airplane-search</v-icon>
          <div class="text-h6 grey--text text--darken-1 mb-2">
            {{ $t("flightModal.noDataAvailable") }}
          </div>
          <div class="text-body-2 grey--text">
            {{ $t("flightModal.noDataDescription") }}
          </div>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions class="pa-3">
          <v-btn
            v-if="booking.flightStatsLink && booking.flightStatsLink !== '#'"
            color="indigo"
            outlined
            block
            :href="booking.flightStatsLink"
            target="_blank"
          >
            <v-icon left>mdi-open-in-new</v-icon>
            {{ $t("flightModal.viewOnFlightStats") }}
          </v-btn>
          <v-btn
            v-else
            color="grey"
            text
            block
            @click="flightModal = false"
          >
            {{ $t("global.dialog.btnCancel") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialogos -->
    <v-dialog v-model="dialog.active" persistent max-width="290">
      <v-card :color="colorDialog" :dark="dialog.error">
        <v-card-title>{{ labelDialog }}</v-card-title>
        <v-card-text>{{ dialog.changeStatusAlert }}</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn
            color="darken-1"
            text
            @click="dialog.active = false"
            v-if="!dialog.error"
          >
            {{ $t("booking.dialog.btnCancel") }}
          </v-btn>
          <v-btn text @click="dialog.action">
            {{ $t("booking.dialog.btnToAccept") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
    <v-snackbar
      :timeout="2000"
      :color="snackBar.variant"
      variant="outlined"
      v-model="snackBar.show"
      location="top right"
    >
      <template v-slot:action="{ attrs }">
        <v-btn color="white" text v-bind="attrs" @click="snackBar.show=false">
          {{ $t("global.snackbar.close") }}
        </v-btn>
      </template>

      {{ snackBar.message }}
    </v-snackbar>
  </div>
</template>

<script>
import { mapActions, mapMutations, mapState } from "vuex";

const leafletMap = () =>
  import(/* webpackChunkName: "Gmap" */ "@/components/Leaflet.vue");
  const gmap = () =>
  import(/* webpackChunkName: "Gmap" */ "@/components/Gmap.vue");
const Loading = () =>
  import(/* webpackChunkName: "Loading" */ "@/components/loading.vue");
export default {
  components: { gmap, Loading,leafletMap },
  data: () => ({
    isLoading: true,
    validBooking: false,
    booking: {},
    map: {
      origin: { lat: 0, lng: -0 },
      destination: { lat: 0, lng: -0 },
    },
    dialog: {
      active: false,
      label: null,
      changeStatusAlert: "",
      action: () => {},
      error: false,
    },
    statesName: [
      "pending",
      "onWayToPickup",
      "atPickup",
      "onWayToDropoff",
      "finished",
    ],
    bookingStatusOpts: {},
    bookingStatus: {},
    steps: {},
    driverMap:"leaflet",//or gmap
    statuses: [
      { value: 'pending', label: 'Ready to start' },
      { value: 'onWayToPickup', label: 'On Way to Pickup' },
      { value: 'atPickup', label: 'Arrived' },
      { value: 'onWayToDropoff', label: 'On Way to dropoff' },
      { value: 'finished', label: 'Finished' }
    ],
    snackBar:{
      show:false,
      message:"",
      variant:"",
    },
    flightModal: false,
    loadingFlightInfo: false,
    showScrollHint: true,
    flightChannel: null,
    flightChannelName: null,
    flightInfoUpdated: false,
  }),
  methods: {
    ...mapActions(['initGpsTracking']),
    ...mapMutations("ui", {
      addNavigationItem: "addNavigationItem",
      clearNavigationItems: "clearNavigationItems",
      removeNavigationItem: "removeNavigationItem",
      openSnackbar: "openSnackbar",
    }),
    ...mapMutations("services", {
      setService: "setService",
      changeStatus: "changeStatus",
      checkin: "checkin",
      addGpcLocation: "addGpcLocation",
    }),
    ...mapActions("ui", {
      disabledNavigation: "disabledNavigation",
      enableddNavigation: "enableddNavigation",
      openQRReaderOnlyEvent: "openQRReaderOnlyEvent",
      activeBackBtn: "activeBackBtn",
      showProgress: "showProgress",
      hideProgress: "hideProgress",
      resetQR: "resetQR",
    }),
    ...mapActions("services", {
      resetService: "resetService",
    }),
    fetchModel() {
      this.isLoading = true;
      this.$axios
        .get(`driver/transport-services/${this.$route.params.id}`)
        .then((res) => {
          this.booking = { ...res.data };
          this.validBooking = true;

          this.map.origin = {
            lat: parseFloat(this.booking.pickup.latitude),
            lng: parseFloat(this.booking.pickup.longitude),
          };
          this.map.destination = {
            lat: parseFloat(this.booking.dropoff.latitude),
            lng: parseFloat(this.booking.dropoff.longitude),
          };

          this.bookingStatus = this.bookingStatusOpts[this.booking.status];

          // Almacenar el indice correcto
          this.changeStatus(
            parseInt(this.findIndexForStatus(this.booking.trackingStatus))
          );

          // Validar que las coordenadas sean correctas
          if (this.isValidCord()) {
            if (
              this.booking.trackingStatus !==
                this.statesName[this.statesName.length - 1] &&
              this.booking.trackingStatus !== "noShow" &&
              this.booking.status === "active"
            ) {
              this.initNavigation();
            }
          } else {
            this.openSnackbar(this.$t("booking.txtErrorCord"));
          }

          this.isLoading = false;

          // Subscribe to flight status updates via Pusher
          this.subscribeToFlightUpdates();
        });
    },
    initNavigation() {
      if (this.isCheckin()) {
        this.checkinAction();
      } else {
        this.tryNextStep(this.stateIndex + 1);
      }
    },
    checkinAction() {
      // Checkin
      this.checkin();
      // Almacenar el Servicio
      this.setService(this.booking);
      // Asignar el ID del status
      this.changeStatus(3);
      // Pasar al paso de checkin
      this.tryNextStep(3);
    },
    tryNextStep(index) {
      if (index <= this.statesName.length - 1) {
        const btn = this.steps[this.statesName[index]];

        // Vaciar la barra de navegacion
        this.clearNavigationItems();

        // Verificar que no se requiera validar.
        if (
          this.statesName[index] === "onWayToDropoff" &&
          !this.serviceCheckin
        ) {
          this.addBtn(
            {
              btnText: "Check In",
              btnIcon: "mdi-account-check-outline",
            },
            null,
            () => {
              this.openQRReaderOnlyEvent();
              // Esperar la respuesta
              this.$bus.once("onDecode", (data) => {
                if (
                  this.id === parseInt(data.id) ||
                  this.booking.id === parseInt(data.id) ||
                  data.check
                ) {
                  // Vaciar la barra de navegacion
                  this.clearNavigationItems();
                  // Indicar que ya se hizo checking
                  this.checkin();
                  // Almacenar el evento
                  this.saveGpcLocationStatus("Checkin");
                  // Agregar el boton
                  this.addBtn(this.steps.onWayToDropoff, index);
                } else {
                  // Notificar el evento
                  this.openSnackbar(this.$t("booking.txtErrorNoMatchQr"));
                }
              });
            },
            true
          );

          // Boton de no show
          this.addBtn(
            {
              btnText: "Not Show",
              btnIcon: "mdi-eye-off-outline",
              changeStatusAlert: this.$t("booking.txtErrorNoShow"),
            },
            null,
            () => {
              const timeBooking = new Date(
                this.booking.pickupDatetime
              ).getTime();
              const currentTime = new Date().getTime();

              // Validar el tiempo de espera segun
              if (this.booking.trip === "A2H") {
                if (currentTime - timeBooking > 60000 * 60) {
                  this.actionNoShow();
                  // Disabled Navigation
                  this.disabledNavigation();
                } else {
                  this.dialogNoshowIncorrect(this.$t("booking.txtError60Min"));
                }
              } else if (
                this.booking.trip === "H2A" ||
                this.booking.trip === "P2P"
              ) {
                if (currentTime - timeBooking > 60000 * 15) {
                  this.actionNoShow();
                  // Disabled Navigation
                  this.disabledNavigation();
                } else {
                  this.dialogNoshowIncorrect(this.$t("booking.txtError15Min"));
                }
              } else {
                this.dialog.changeStatusAlert = this.$(
                  "booking.txtErrorNoTrip"
                );
                this.dialog.active = true;
              }
            }
          );
        } else if (this.statesName[index] === "atPickup") {
          this.addBtn(btn, index, async () => {
            this.dialog.active = false;
            this.actionStep(index);
          });
        } else {
          // Agregar el elemento correspondiente
          this.addBtn(btn, index);
        }

        // Mostrar el navigationButtom
        this.enableddNavigation();
      } else {
        // Vaciar la barra de navegacion
        this.clearNavigationItems();
        // Desabilitat el menu
        this.disabledNavigation();
      }
    },
    dialogNoshowIncorrect(text) {
      this.dialog.active = false;
      this.dialog.label = this.$t("booking.txtNoShowNotAllowed");
      this.dialog.changeStatusAlert = text + this.$t("booking.txtNoShowPlus");
      this.dialog.error = true;
      this.dialog.active = true;
      this.dialog.action = () => {
        this.dialog.active = false;
      };
    },
    async actionNoShow() {
      this.resetQR();
      this.resetService();
      this.$router.push({
        name: "noshow",
        params: { id: this.booking.id },
      });
    },
    async actionStep(index) {
      try {
        if(!this.$store.state.ui.online) {
          this.openSnackbar("Offline");
          return;
        } 
        // Mostrar la barra de progreso
        this.showProgress();
        let position = null;
        try {
          position =await this.getLocation();  // Await the geolocation
        } catch (error) {
          console.error(error);
        }
        // Guardar la ubicacion
        await this.doNextStep(this.statesName[index], {
          latitude: position.latitude,
          longitude: position.longitude,
          velocityKmh: position.speed || 0,
        });

        if (this.statesName[index] === "finished") {
          // Reset
          this.resetData();
        } else if (this.statesName[index] === "onWayToPickup") {
          // Almacenar el Servicio
          this.setService(this.booking);
        } else {
          // Asignar el ID del status
          this.changeStatus(index + 1);
        }
        this.tryNextStep(index + 1);
      } catch (error) {
        console.log(error)
        this.hideProgress();
        if (error.gps) {
          this.openSnackbar(this.$t("booking.txtGpsError"));
        } else {
          let errorTxt = this.$t("booking.txtErrorChangeStatus");
          if (error.response && error.response.status === 403) {
            const data = error.response.data;
            if (data && data.errorCode === 20) {
              errorTxt = `${this.$t("booking.txtErrorServiceInProcess")} ${
                data.serviceNumber
              }`;
            }
          }
          this.openSnackbar(errorTxt);
        }
      }
    },
    addBtn(btn, index, action, notDialog) {
      // Agregar el boton al navigationButtom
      this.addNavigationItem({
        name: btn.btnText,
        icon: btn.btnIcon,
        type: "action",
        action: () => {
          // Validar si requiere lanzar o no el dialogo
          if (notDialog) {
            if (action) {
              action();
            } else {
              this.actionStep(index);
            }
          } else {
            this.dialog = {
              ...this.dialog,
              ...btn,
              ...{
                action: () => {
                  this.dialog.active = false;
                  if (action) {
                    action();
                  } else {
                    this.actionStep(index);
                  }
                },
                changeStatusAlert: this.stringReplace(btn.changeStatusAlert),
              },
              label: null,
              error: false,
              active: true,
            };
          }
        },
      });
    },
    doNextStep(status, tracking) {
      const url = `driver/transport-services/${this.booking.id}/change-tracking-status`;
      return new Promise((rr, ff) => {
        this.$axios
          .put(url, { status, tracking })
          .then((resp) => {
            this.bookingStatus = this.bookingStatusOpts[resp.data.status];
            this.booking.trackingStatus = resp.data.trackingStatus;
            rr(resp.data);
          })
          .catch((err) => {
            ff(err);
          })
          .finally(() => {
            this.hideProgress();
            this.showNotification(this.$t("global.savedSuccessfully"));
          });
      });
    },
    findIndexForStatus(state) {
      for (let i in this.statesName) {
        if (this.statesName[i] === state) {
          return i;
        }
      }
    },
    isValidCord() {
      return this.booking.pickup.address !== null;
    },
    isCheckin() {
      if (this.$route.query.checkin) {
        return true;
      } else {
        return false;
      }
    },
    stringReplace(text) {
      return text
        .replace("$pickup", this.booking.pickup.name)
        .replace("$dropoff", this.booking.dropoff.name);
    },
    chat(id) {
      setTimeout(() => {
        this.$router.push({ params: { id }, name: "Chat" });
      }, 500);
    },
    async saveGpcLocationStatus(status) {
      const position = await this.getLocation();
      // Guardar en store
      this.addGpcLocation({
        status,
        latitude: position.latitude,
        longitude: position.longitude,
      });
    },
    async getLocation() {
      try {
        await this.initGpsTracking();
        return this.geolocationData;
      } catch (err) {
        console.warn("⚠️ Could not get location", err);
      }
    },

    resetData() {
      this.resetQR();
      this.resetService();
      this.disabledNavigation();
      this.dialog = {
        active: false,
        label: "",
        changeStatusAlert: "",
        action: () => {},
      };
    },
    showNotification(msg,variant="success"){
      this.snackBar.show=true;
      this.snackBar.message=msg;
      this.snackBar.variant=variant;
    },
    handleScroll() {
      this.showScrollHint = window.scrollY < 50;
    },
    scrollToBottom() {
      window.scrollTo({
        top: document.body.scrollHeight,
        behavior: 'smooth'
      });
    },
    openFlightModal() {
      this.flightModal = true;
    },
    reloadFlightInfo() {
      this.loadingFlightInfo = true;
      this.$axios
        .get(`driver/transport-services/${this.$route.params.id}`)
        .then((res) => {
          this.booking.flightInfo = res.data.flightInfo;
          this.openSnackbar(this.$t("global.savedSuccessfully"));
        })
        .catch(() => {
          this.openSnackbar(this.$t("forgot.txTunexpectedError"));
        })
        .finally(() => {
          this.loadingFlightInfo = false;
        });
    },
    subscribeToFlightUpdates() {
      try {
        // Check if echo plugin is available
        if (typeof this.$echo !== 'function') {
          console.warn('Echo plugin not available');
          return;
        }

        const echo = this.$echo();
        if (!echo || !this.booking.id || !this.user?.id) return;

        // Save channel name for later unsubscription
        this.flightChannelName = `driver-${this.user.id}`;

        // Subscribe to private channel for this booking's flight updates
        this.flightChannel = echo.private(this.flightChannelName)
          .listen('.flight-status-updated', (data) => {
            this.handleFlightStatusUpdate(data);
          })
          .error((error) => {
            console.warn('Pusher subscription error:', error);
            this.flightChannel = null;
            this.flightChannelName = null;
          });
      } catch (error) {
        console.warn('Failed to subscribe to flight updates:', error);
        this.flightChannel = null;
        this.flightChannelName = null;
      }
    },
    unsubscribeFromFlightUpdates() {
      try {
        // Disconnect Echo entirely to clean up Pusher connection
        if (typeof this.$echoDisconnect === 'function') {
          this.$echoDisconnect();
        }
      } catch (error) {
        console.warn('Failed to unsubscribe from flight updates:', error);
      } finally {
        // Always clean up local state
        this.flightChannel = null;
        this.flightChannelName = null;
      }
    },
    handleFlightStatusUpdate(data) {
      // Update flight info with the new data from Pusher
      if (data.flightInfo) {
        this.booking.flightInfo     = data.flightInfo;
        this.booking.pickupDatetime = data.pickupDatetime;
        this.booking.flightDatetime = data.flightDatetime;

        // Trigger flash animation
        this.flightInfoUpdated = true;
        setTimeout(() => {
          this.flightInfoUpdated = false;
        }, 1500);
      }
      // Show notification to driver
      this.showNotification(this.$t("flightModal.flightUpdated"), "info");
    },
    flightStatusColor(flightInfo) {
      if (!flightInfo) return "grey";

      // Handle special statuses first
      if (flightInfo.status === "cancelled") return "red darken-3";
      if (flightInfo.status === "landed") return "green";

      const minutes = flightInfo.delayMinutes || 0;
      const threshold = flightInfo.minDelayThreshold || 15;

      if (minutes >= threshold) {
        return "red darken-1"; // delayed
      } else if (minutes <= -threshold) {
        return "orange darken-1"; // early
      } else {
        return "green"; // on time
      }
    },
    flightStatusIcon(flightInfo) {
      if (!flightInfo) return "mdi-airplane-alert";

      // Handle special statuses first
      if (flightInfo.status === "cancelled") return "mdi-airplane-off";
      if (flightInfo.status === "landed") return "mdi-airplane-landing";

      const minutes = flightInfo.delayMinutes || 0;
      const threshold = flightInfo.minDelayThreshold || 15;

      if (minutes >= threshold) {
        return "mdi-airplane-clock"; // delayed
      } else if (minutes <= -threshold) {
        return "mdi-airplane"; // early
      } else {
        return "mdi-airplane-check"; // on time
      }
    },
    flightStatusText(flightInfo) {
      if (!flightInfo) return "";

      // Handle special statuses first
      if (flightInfo.status === "cancelled") return this.$t("flightStatus.cancelled");
      if (flightInfo.status === "landed") return this.$t("flightStatus.landed");

      const minutes = flightInfo.delayMinutes || 0;
      const threshold = flightInfo.minDelayThreshold || 15;

      if (minutes >= threshold) {
        // Delayed
        return this.$t("flightStatus.delayedMinutes", { minutes });
      } else if (minutes <= -threshold) {
        // Early
        return this.$t("flightStatus.earlyMinutes", { minutes: Math.abs(minutes) });
      } else {
        // On time (within threshold)
        return this.$t("flightStatus.on_time");
      }
    },
    getFlightStatusClass(flightInfo) {
      if (!flightInfo) return "unknown";
      if (flightInfo.status === "cancelled") return "cancelled";
      if (flightInfo.status === "landed") return "landed";

      const minutes = flightInfo.delayMinutes || 0;
      const threshold = flightInfo.minDelayThreshold || 15;

      if (minutes >= threshold) return "delayed";
      if (minutes <= -threshold) return "early";
      return "ontime";
    },
  },
  computed: {
    ...mapState("auth", {
      user: (state) => state.user,
    }),
    ...mapState(["geolocationData"]),
    ...mapState("services", {
      stateIndex: (state) => state.stateIndex,
      service: (state) => state.data,
      id: (state) => state.id,
      serviceCheckin: (state) => state.checkin,
    }),
    isPending() {
      return this.booking.trackingStatus === "pending";
    },
    validCoords() {
      return (
        this.validBooking &&
        this.booking.pickup.latitude !== null &&
        this.booking.pickup.longitude !== null &&
        this.booking.dropoff.latitude !== null &&
        this.booking.dropoff.longitude !== null
      );
    },
    colorDialog() {
      if (this.dialog.error) {
        return "red darken-3";
      }
      return "white";
    },
    labelDialog() {
      if (this.dialog.label === null) {
        return this.$t("booking.txtChangeStatus");
      } else {
        return this.dialog.label;
      }
    },
    clientName() {
      let name =
        this.booking.reservation.client.firstName +
        " " +
        this.booking.reservation.client.lastName;
      if (name.length > 50) {
        return name.substring(0, 17) + "...";
      } else {
        return name;
      }
    },
    currentStepIndex() {
      try{
        let trackingStatus = this.booking.trackingStatus;
        let index =  this.statuses.findIndex(status => status.value === trackingStatus);
        return index+1;
      }catch(err){
        return null;
      }
      
    },
    formattedPickupDate() {
      const pickupDate = this.$moment(this.booking.pickupDatetime);
      return pickupDate.isSame(this.$moment(), 'day') ? this.$t("global.today") : pickupDate.format('D MMM YYYY');
    },
    isToday(){
      const pickupDate = this.$moment(this.booking.pickupDatetime);
      return pickupDate.isSame(this.$moment(), 'day') ? true: false;
    },
    hasFlightData() {
      const info = this.booking.flightInfo;
      if (!info) return false;
      return info.status || info.estimatedLanding || info.originAirport || info.progressPercent !== undefined;
    },
  },
  mounted() {
    // Asignaciones
    this.steps = {
      pending: {
        status: "pending",
        btnText: this.$t("booking.steps.pending.btnText"),
        btnIcon: "mdi-car-estate",
        label: this.$t("booking.steps.pending.txtLabel"),
        changeStatusAlert: this.$t(
          "booking.steps.pending.txtChangeStatusAlert"
        ),
      },
      onWayToPickup: {
        status: "onWayToPickup",
        btnText: this.$t("booking.steps.onWayToPickup.btnText"),
        btnIcon: "mdi-car-estate",
        label: this.$t("booking.steps.onWayToPickup.txtLabel"),
        changeStatusAlert: this.$t(
          "booking.steps.onWayToPickup.txtChangeStatusAlert"
        ),
      },
      atPickup: {
        status: "atPickup",
        btnText: this.$t("booking.steps.atPickup.btnText"),
        btnIcon: "mdi-car-estate",
        label: this.$t("booking.steps.atPickup.txtLabel"),
        changeStatusAlert: this.$t(
          "booking.steps.atPickup.txtChangeStatusAlert"
        ),
      },
      onWayToDropoff: {
        status: "onWayToDropoff",
        btnText: this.$t("booking.steps.onWayToDropoff.btnText"),
        btnIcon: "mdi-car-estate",
        label: this.$t("booking.steps.onWayToDropoff.txtLabel"),
        changeStatusAlert: this.$t(
          "booking.steps.onWayToDropoff.txtChangeStatusAlert"
        ),
      },
      finished: {
        status: "finished",
        btnText: this.$t("booking.steps.finished.btnText"),
        btnIcon: "mdi-car-estate",
        label: this.$t("booking.steps.finished.txtLabel"),
        changeStatusAlert: this.$t(
          "booking.steps.finished.txtChangeStatusAlert"
        ),
      },
    };

    this.bookingStatusOpts = {
      active: {
        color: "success",
        label: this.$t("booking.bookingStatusOpts.txtActive"),
      },
      noShow: {
        color: "primary",
        label: this.$t("booking.bookingStatusOpts.txtNoShow"),
      },
      noProvided: {
        color: "warning",
        label: this.$t("booking.bookingStatusOpts.txtNoProvided"),
      },
      canceled: {
        color: "error",
        label: this.$t("booking.bookingStatusOpts.txtCanceled"),
      },
      done: {
        color: "success",
        label: this.$t("booking.bookingStatusOpts.txtDone"),
      },
    };

    // =>
    this.disabledNavigation();
    this.clearNavigationItems();
    this.activeBackBtn();
    this.fetchModel();

    // Scroll listener para ocultar indicador
    window.addEventListener('scroll', this.handleScroll);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    // Unsubscribe from Pusher channel
    this.unsubscribeFromFlightUpdates();
  },
};
</script>

<style scoped>

/* Booking Header Styles */
.booking-header {
  background: #fff;
  border-bottom: 1px solid #eee;
}

/* Current Step Display */
.current-step-container {
  padding: 12px 16px;
  background: #fafafa;
  text-align: center;
}

.step-progress-dots {
  display: flex;
  justify-content: center;
  gap: 6px;
  margin-bottom: 8px;
}

.progress-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #e0e0e0;
  transition: all 0.2s ease;
}

.dot-completed {
  background: #4caf50;
}

.dot-active {
  background: #6200ea;
  box-shadow: 0 0 0 3px rgba(98, 0, 234, 0.2);
}

.current-step-name {
  font-size: 15px;
  font-weight: 600;
  color: #6200ea;
}

/* Service Info Card */
.service-info-card {
  padding: 16px;
  background: #fff;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 12px;
}

.datetime-row {
  justify-content: space-between;
  margin-bottom: 12px;
}

.datetime-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.info-item {
  display: flex;
  align-items: center;
  gap: 6px;
}

.info-value {
  font-size: 17px;
  font-weight: 600;
  color: #333;
}

.today-highlight {
  color: #6200ea;
}

.info-divider {
  width: 1px;
  height: 20px;
  background: #e0e0e0;
}

/* Flight Status Pill - Compact */
.flight-status-pill {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
}

.flight-status-pill .v-icon {
  font-size: 12px !important;
}

.flight-ontime {
  background: rgba(76, 175, 80, 0.12);
  color: #2e7d32;
}

.flight-delayed {
  background: rgba(244, 67, 54, 0.12);
  color: #c62828;
}

.flight-early {
  background: rgba(255, 152, 0, 0.12);
  color: #e65100;
}

.flight-landed {
  background: rgba(33, 150, 243, 0.12);
  color: #1565c0;
}

.flight-cancelled {
  background: rgba(158, 158, 158, 0.15);
  color: #616161;
}

.flight-unknown {
  background: rgba(117, 117, 117, 0.12);
  color: #616161;
}

/* Client Row */
.client-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  background: #f5f5f5;
  padding: 10px 12px;
  border-radius: 10px;
}

.client-info {
  display: flex;
  align-items: center;
  gap: 10px;
  flex: 1;
  min-width: 0;
}

.client-avatar {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, #667eea 0%, #764ba2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.client-name {
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.chat-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 14px;
  background: #6200ea;
  border: none;
  border-radius: 8px;
  color: #fff;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s ease;
  flex-shrink: 0;
}

.chat-btn:active {
  transform: scale(0.95);
  background: #5000d0;
}

.chat-btn span {
  display: none;
}

@media (min-width: 360px) {
  .chat-btn span {
    display: inline;
  }
}

/* Reservation Section - Minimalist */
.reservation-section {
  padding: 8px 16px;
}

.reservation-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 3px 0;
}

.res-id {
  font-size: 14px;
  font-weight: 600;
  color: #333;
}

.status-pill {
  padding: 4px 10px;
  border-radius: 4px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
}

.status-active {
  background: rgba(76, 175, 80, 0.15);
  color: #2e7d32;
}

.status-done {
  background: rgba(76, 175, 80, 0.15);
  color: #2e7d32;
}

.status-noShow {
  background: rgba(98, 0, 234, 0.1);
  color: #6200ea;
}

.status-noProvided {
  background: rgba(255, 152, 0, 0.15);
  color: #e65100;
}

.status-canceled {
  background: rgba(244, 67, 54, 0.12);
  color: #c62828;
}

.pax-inline {
  display: flex;
  align-items: center;
  gap: 12px;
}

.pax-total {
  font-size: 14px;
  font-weight: 600;
  color: #6200ea;
}

.pax-breakdown {
  display: flex;
  align-items: center;
  font-size: 12px;
  color: #555;
}

.pax-breakdown .v-icon {
  margin-right: 2px;
}

.res-service {
  font-size: 13px;
  color: #555;
}

.res-channel {
  font-size: 13px;
  font-weight: 600;
  color: #6200ea;
}

/* Route Section */
.route-section {
  padding: 12px 16px;
}

.route-wrapper {
  display: flex;
  position: relative;
}

.route-line {
  position: absolute;
  left: 4px;
  top: 8px;
  height: calc(100% - 32px);
  width: 2px;
  background: #e0e0e0;
}

.route-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.route-point {
  display: flex;
  align-items: flex-start;
  gap: 14px;
}

.route-dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
  margin-top: 3px;
}

.dot-origin {
  background: #6200ea;
}

.dot-destination {
  background: #fff;
  border: 2px solid #999;
}

.route-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.route-label {
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
}

.route-name {
  font-size: 14px;
  color: #333;
  line-height: 1.3;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

/* Update flash animation */
.info-updated {
  animation: flashUpdate 1.5s ease-out;
}

@keyframes flashUpdate {
  0% {
    background-color: rgba(98, 0, 234, 0.2);
    box-shadow: 0 0 8px rgba(98, 0, 234, 0.4);
  }
  50% {
    background-color: rgba(98, 0, 234, 0.1);
    box-shadow: 0 0 4px rgba(98, 0, 234, 0.2);
  }
  100% {
    background-color: transparent;
    box-shadow: none;
  }
}

/* Details Section */
.details-section {
  padding: 0 16px 12px;
}

.notes-row {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 10px 12px;
  background: #f5f5f5;
  border-radius: 8px;
  margin-top: 8px;
}

.notes-content {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
  flex: 1;
}

.notes-label {
  font-size: 11px;
  color: #666;
  text-transform: uppercase;
}

.notes-text {
  font-size: 13px;
  color: #444;
  line-height: 1.4;
  word-wrap: break-word;
  overflow-wrap: break-word;
}

.scroll-hint {
  position: fixed;
  bottom: 80px;
  right: 16px;
  width: 32px;
  height: 32px;
  background: rgba(98, 0, 234, 0.8);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  animation: bounce 1.5s infinite;
  z-index: 10;
  box-shadow: 0 2px 8px rgba(0,0,0,0.15);
  cursor: pointer;
}

@keyframes bounce {
  0%, 100% { transform: translateY(0); }
  50% { transform: translateY(4px); }
}
</style>
<style>
.v-bottom-navigation button.items_1 .mdi-car-estate {
  margin-right: 10px; /* Space between the icon and text */
  animation: moveIcon 3s ease-in-out infinite; /* Infinite back-and-forth animation */
}

/* Keyframes to move the icon back and forth */
@keyframes moveIcon {
  0% {
    transform: translateX(0); /* Start at the original position */
  }
  50% {
    transform: translateX(12px); /* Move to the right */
  }
  100% {
    transform: translateX(0); /* Move back to the left */
  }
}


</style>

<style scoped>
.pulse-icon {
  animation: pulse 1.5s infinite ease-in-out;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  50% {
    transform: scale(1.2);
    opacity: 0.6;
  }
  100% {
    transform: scale(1);
    opacity: 1;
  }
}

/* Flight Card */
.flight-card {
  background: #fff;
  border: 1px solid #e5e5e5;
  border-radius: 12px;
  padding: 14px;
  cursor: pointer;
  transition: all 0.15s ease;
}

.flight-card:active {
  transform: scale(0.98);
  background: #fafafa;
}

.flight-card-compact {
  padding: 10px 14px;
}

.flight-card-compact .flight-card-header {
  margin-bottom: 0;
}

.flight-card-compact .flight-number {
  font-size: 15px;
}

.flight-time-inline {
  display: flex;
  align-items: center;
  font-size: 14px;
  font-weight: 600;
  color: #333;
  margin-right: 4px;
}

.flight-card-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.flight-main-info {
  display: flex;
  align-items: center;
  flex: 1;
}

.flight-number {
  font-size: 18px;
  font-weight: 600;
  color: #1a1a1a;
}

.flight-card-body {
  border-top: 1px solid #f0f0f0;
  padding-top: 12px;
}

.flight-times {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 10px;
}

.flight-time-item {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 13px;
}

.flight-time-label {
  color: #666;
  flex: 1;
}

.flight-time-value {
  font-weight: 600;
  color: #333;
}

.flight-time-value.highlight {
  color: #1565c0;
}

.flight-meta {
  display: flex;
  gap: 16px;
  flex-wrap: wrap;
}

.flight-meta-item {
  display: flex;
  align-items: center;
  gap: 4px;
  font-size: 12px;
  color: #666;
  background: #f5f5f5;
  padding: 4px 8px;
  border-radius: 6px;
}

/* Flight Modal Styles */
.flight-modal-card {
  border-radius: 16px !important;
  overflow: hidden;
}

.flight-modal-header {
  background: linear-gradient(135deg, #3f51b5 0%, #5c6bc0 100%);
  color: white;
}

.flight-modal-header .text-h6 {
  color: white;
}

.flight-modal-header .text-caption {
  color: rgba(255, 255, 255, 0.8);
}

.flight-modal-header .v-btn {
  color: white !important;
}

.flight-status-section {
  background: linear-gradient(135deg, #f5f5f5 0%, #ffffff 100%);
}

.flight-metadata {
  background-color: #fafafa;
  border-top: 1px solid #f0f0f0;
}

/* Fix para nombres largos de pickup/dropoff */
.v-list-item__title {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
  line-height: 1.4 !important;
}

.v-list-item__subtitle {
  white-space: normal !important;
  overflow: visible !important;
  text-overflow: unset !important;
}
</style>
